/* ============================================================
   Lagomlo — ai-provider.js (Shopify theme)
   AI image generation adapter (port of preview/js/ai-provider.js).
   - mode 'mock'    : canvas-generated placeholder images (fully offline demo)
   - mode 'volcano' : Volcano Ark (火山方舟) image API — configured later;
                      API key must live on a backend proxy, NEVER in frontend.

   Theme wiring: the ai-studio section writes window.Lagomlo.ai
   { imagesPerRun, freeCreditsOnSignup, showCredits } so the number of
   candidates per run follows the section setting.

   Exposed: window.AIProvider.generate(prompt, opts) -> Promise<{images:[dataUrl,...], meta}>
   ============================================================ */
(function (global) {
  'use strict';

  var CONFIG = {
    mode: 'volcano', // 'mock' | 'volcano' — 2026-08-25 已切真实模式（Cloudflare 免费生图）
    volcano: {
      baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
      model: 'doubao-seedream-5-0-pro-260628', // paid image model (Volcano, needs balance)
      proxyUrl: 'https://lagomlo-ai-proxy.446234987.workers.dev/ai/free-generate' // Cloudflare Workers AI 免费生图
      // apiKey: ''  // NEVER hardcode in frontend — route via backend proxy
    },
    imagesPerRun: 4
  };

  // Read the per-section setting (1-8) written by ai-studio.liquid.
  if (global.Lagomlo && global.Lagomlo.ai && global.Lagomlo.ai.imagesPerRun) {
    var n = parseInt(global.Lagomlo.ai.imagesPerRun, 10);
    if (!isNaN(n) && n >= 1 && n <= 8) CONFIG.imagesPerRun = n;
  }

  function getMode() {
    // allow runtime override for testing: localStorage 'lagomlo.aiMode'
    try {
      var m = global.localStorage.getItem('lagomlo.aiMode');
      if (m === 'mock' || m === 'volcano') return m;
    } catch (e) {}
    return CONFIG.mode;
  }

  /* ---------- Mock generator (deterministic, offline) ---------- */
  // Builds N distinct warm-gradient "art" tiles seeded by the prompt so the
  // demo feels real (colors/sunburst/pattern vary per candidate).
  function hashString(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function paletteFor(seed) {
    var palettes = [
      ['#F2E9DD', '#E9C9BC', '#C4704F', '#3E2F26'],
      ['#DCE5D8', '#8C9A7E', '#5A6B4A', '#2F3B2A'],
      ['#EAD9F0', '#C9A7D8', '#8E5FA8', '#4A2E5C'],
      ['#FBE9D0', '#F2C46C', '#D98E32', '#7A4A1B'],
      ['#D9E8F2', '#9CC3E0', '#5B8FB9', '#2E4A66'],
      ['#F5DCDC', '#E58F8F', '#B84A4A', '#5C2323'],
      ['#E8F0F0', '#A8C8C8', '#5E8C8C', '#2E4A4A'],
      ['#F0E8DC', '#D4BFA0', '#A8895F', '#5C4628']
    ];
    return palettes[seed % palettes.length];
  }

  function drawMock(canvas, seed, prompt, idx) {
    var w = canvas.width = 768;
    var h = canvas.height = 768;
    var ctx = canvas.getContext('2d');
    var pal = paletteFor(seed);
    var rnd = function (n) { return (hashString(String(seed) + '|' + idx + '|' + n)) % 1000 / 1000; };

    // background gradient
    var g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, pal[0]);
    g.addColorStop(0.6, pal[1]);
    g.addColorStop(1, pal[2]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // soft "sunburst" arcs
    ctx.globalAlpha = 0.18;
    for (var a = 0; a < 6; a++) {
      ctx.beginPath();
      ctx.arc(w * (0.3 + rnd(a) * 0.4), h * (0.3 + rnd(a + 6) * 0.4), 90 + rnd(a + 12) * 160, 0, Math.PI * 2);
      ctx.fillStyle = pal[3];
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // central shape — abstract "blanket fold" bands
    ctx.globalAlpha = 0.5;
    for (var b = 0; b < 5; b++) {
      var y = h * (0.25 + b * 0.12 + rnd(b + 20) * 0.05);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(w * 0.3, y - 40 - rnd(b + 30) * 60, w * 0.7, y + 40 + rnd(b + 40) * 60, w, y);
      ctx.lineWidth = 18 + rnd(b + 50) * 30;
      ctx.strokeStyle = pal[3];
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // watermark-ish caption so each candidate differs
    ctx.fillStyle = 'rgba(62,47,38,0.55)';
    ctx.font = '600 22px Georgia, serif';
    ctx.textAlign = 'center';
    var tag = (prompt || 'Lagomlo AI').slice(0, 34);
    ctx.fillText('\u201C' + tag + '\u201D', w / 2, h - 40);
    ctx.font = '500 16px system-ui, sans-serif';
    ctx.fillText('AI concept ' + (idx + 1), w / 2, h - 14);

    return canvas.toDataURL('image/jpeg', 0.85);
  }

  function mockGenerate(prompt, opts) {
    var seed = hashString(prompt + '|' + (opts && opts.style || '') + '|' + Date.now());
    var canvas = document.createElement('canvas');
    var images = [];
    for (var i = 0; i < CONFIG.imagesPerRun; i++) {
      images.push(drawMock(canvas, seed + i * 7919, prompt, i));
    }
    return Promise.resolve({ images: images, meta: { mode: 'mock', prompt: prompt } });
  }

  /* ---------- Volcano Ark adapter (Cloudflare Worker proxy) ----------
     Frontend posts { prompt, style, n } to our Cloudflare Worker:
        POST https://lagomlo-ai-proxy.446234987.workers.dev/ai/free-generate
     Worker uses Cloudflare Workers AI FLUX.1-schnell (free 10,000 Neurons/day).
     Set CONFIG.volcano.proxyUrl (or window.Lagomlo.ai.volcanoProxy).
  */
  function volcanoGenerate(prompt, opts) {
    var proxyUrl = (global.Lagomlo && global.Lagomlo.ai && global.Lagomlo.ai.volcanoProxy) || CONFIG.volcano.proxyUrl;
    if (!proxyUrl) {
      return Promise.reject(new Error('Volcano backend not configured yet — switch AI mode to "mock" in localStorage (lagomlo.aiMode).'));
    }
    return fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: CONFIG.volcano.model,
        prompt: prompt,
        style: opts && opts.style,
        n: CONFIG.imagesPerRun
      })
    })
      .then(function (r) { if (!r.ok) throw new Error('AI request failed: ' + r.status); return r.json(); })
      .then(function (data) { return { images: data.images, meta: { mode: 'volcano', prompt: prompt } }; });
  }

  function generate(prompt, opts) {
    return getMode() === 'volcano' ? volcanoGenerate(prompt, opts) : mockGenerate(prompt, opts);
  }

  global.AIProvider = {
    generate: generate,
    getMode: getMode,
    config: CONFIG,
    imagesPerRun: CONFIG.imagesPerRun
  };
})(window);
